import { scrollToHash } from '~/lib/browser/scroll'
import { createFileRoute } from '@tanstack/react-router'
import { cacheHeaders } from '~/lib/site/cache'
import { SOCIAL_LINKS } from '~/lib/site/constants'
import { LOGO_URL, SITE_NAME, SITE_URL, jsonLd } from '~/lib/site/seo'
import { Button } from '~/components/ui/Button'
import { Container } from '~/components/ui/Container'
import { SectionHeading } from '~/components/ui/SectionHeading'
import { Reveal } from '~/components/ui/Reveal'
import { DonateSignupBand } from '~/components/sections/DonateSignupBand'
import { UpcomingEvents } from '~/components/UpcomingEvents'
import { Img } from '~/components/ui/Img'
import { MediaImg } from '~/components/ui/MediaImg'
import { InlineHtml } from '~/components/ui/InlineHtml'
import { getHomePage } from '~/lib/content/content'
import type { HomePage } from '~/lib/content/content'
import jump from '~/assets/images/hero-jump.png?responsive'
import reindeer from '~/assets/images/hero-reindeer.jpg?responsive'
import rescuedPigs from '~/assets/images/hero-rescued-pigs.png?responsive'
import march from '~/assets/images/hero-march.jpg?responsive'
import sanctuaryPigs from '~/assets/images/hero-sanctuary-pigs.jpg?responsive'
import duckFarm from '~/assets/images/hero-duck-farm.jpg?responsive'
import bezos from '~/assets/images/hero-bezos-disruption.jpg?responsive'
import mexico from '~/assets/images/hero-mexico-protest.png?responsive'

export const Route = createFileRoute('/')({
  loader: () => getHomePage(),
  headers: () => cacheHeaders.content,
  head: () => ({
    scripts: [
      jsonLd({
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        alternateName: 'DxE',
        url: SITE_URL,
        logo: LOGO_URL,
        description:
          'A global grassroots network of animal rights activists working for social and political change for animals in one generation.',
        sameAs: SOCIAL_LINKS.map((s) => s.href),
      }),
    ],
  }),
  component: Home,
})

function Home() {
  const { featured } = Route.useLoaderData()
  return (
    <>
      <Hero />
      <FeaturedCampaign {...featured} />
      <section className="pt-20 pb-[50px] md:pb-20">
        <Container>
          <SectionHeading className="mb-2.5">Upcoming events</SectionHeading>
          <UpcomingEvents />
          <div className="flex justify-center pt-10">
            <Button href="/events" variant="outline">
              View more events
            </Button>
          </div>
        </Container>
      </section>
      <DonateSignupBand />
    </>
  )
}

function Hero() {
  return (
    <section className="bg-surface pt-[60px] pb-5 md:pb-[50px] lg:pt-5 lg:pb-10">
      <Container className="mt-[7px] grid grid-cols-2 items-center justify-center gap-[3px] md:gap-1.5 lg:grid-cols-[1fr_2fr] lg:grid-rows-[1fr_auto_auto] lg:items-stretch lg:gap-x-1.5 lg:gap-y-1.5">
        <div className="relative col-span-2 lg:col-span-1 lg:self-baseline">
          <h1 className="mb-[15px] mt-0 text-[35px] leading-10 font-extrabold text-fg min-[480px]:mt-5 min-[480px]:text-[53px] min-[480px]:leading-[65px] lg:leading-[87px]">
            Until every animal is free
          </h1>
          <p className="mb-0 text-lg leading-[26px] md:text-base md:leading-tight">
            Direct Action Everywhere is a global network of activists working to
            achieve revolutionary social and political change for animals in one
            generation.
          </p>
          <a
            href="#Push"
            onClick={scrollToHash}
            className="mt-[15px] block w-16 min-[480px]:mt-[50px]"
            aria-label="Scroll down"
          >
            <Img
              image={jump}
              sizes="64px"
              priority
              width={64}
              height={64}
              alt=""
              className="rounded-full shadow-[0_4px_10px_#0000000f]"
            />
          </a>
        </div>

        {/* pointer-events-none: this block overlaps the scroll-down link on mobile. */}
        <Reveal className="pointer-events-none col-span-2 row-start-2 -mt-[100px] flex items-end justify-between max-[479px]:-mt-[20%] max-[479px]:justify-end lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:mt-[100px]">
          <Img
            image={reindeer}
            sizes="(min-width: 992px) 16vw, 23vw"
            priority
            alt=""
            className="mr-1.5 w-1/4 min-w-0 rounded max-[479px]:mr-[3px]"
          />
          <Img
            image={rescuedPigs}
            sizes="(min-width: 992px) 46vw, (min-width: 480px) 68vw, 54vw"
            priority
            alt="Rescued pigs"
            className="w-3/4 min-w-0 rounded object-cover max-[479px]:w-[60%]"
          />
        </Reveal>

        <Reveal
          delay={150}
          className="row-start-3 flex flex-col items-end self-start lg:row-start-2 lg:-mt-[28%] lg:self-end"
        >
          <Img
            image={march}
            sizes="(min-width: 992px) 30vw, (min-width: 480px) 45vw, 200px"
            priority
            alt="Animal rights march"
            className="mb-1.5 w-[428px] rounded max-[479px]:mb-[3px] max-[479px]:w-[200px]"
          />
          <Img
            image={sanctuaryPigs}
            sizes="214px"
            priority
            alt="Sanctuary pigs"
            className="w-[214px] max-w-[75%] rounded min-[768px]:max-w-full max-md:min-[480px]:max-w-[66%]"
          />
        </Reveal>

        <Reveal
          delay={300}
          className="row-start-3 flex self-start lg:col-start-2 lg:row-start-2 lg:self-auto"
        >
          <Img
            image={duckFarm}
            sizes="(min-width: 992px) 30vw, 36vw"
            priority
            alt="Animal rights activist with a duck that died at Reichardt Duck Farm"
            className="w-[428px] rounded object-cover max-lg:mr-[19%] max-lg:w-4/5"
          />
          <div className="flex flex-col items-start overflow-hidden max-lg:hidden">
            <Img
              image={bezos}
              sizes="25vw"
              alt="Jeff Bezos, CEO of Amazon, disrupted by an activist"
              className="ml-1.5 w-[428px] max-w-[80%] rounded"
            />
            <Img
              image={mexico}
              sizes="16vw"
              alt="Protesta en Mexico"
              className="mt-1.5 w-[210px] ml-1.5 max-w-[80%] rounded"
            />
          </div>
        </Reveal>
      </Container>
    </section>
  )
}

// The featured campaign is edited in content/home.json (docs/content.md).
function FeaturedCampaign({
  title,
  html,
  image,
  imageAlt,
  href,
  cta,
}: HomePage['featured']) {
  const link = /^https?:\/\//.test(href)
    ? { target: '_blank', rel: 'noreferrer' }
    : {}
  return (
    <section id="Push" className="mt-10 bg-surface-alt">
      <div className="mx-auto max-w-[1900px] lg:grid lg:grid-cols-2">
        <a href={href} {...link} className="block self-center bg-royal">
          <MediaImg
            image={image}
            sizes="(min-width: 1900px) 950px, (min-width: 992px) 50vw, 100vw"
            alt={imageAlt}
            className="block w-full object-cover"
          />
        </a>
        <div className="max-w-[666px] self-center px-[25px] pt-2.5 pb-[25px] min-[480px]:px-10 min-[480px]:pb-10 lg:pb-[30px] max-lg:max-w-full">
          <SectionHeading className="mt-5 mb-2.5">{title}</SectionHeading>
          <p className="mb-0 text-base leading-[26px] min-[480px]:text-lg">
            <InlineHtml html={html} />
          </p>
          <div className="mt-5">
            <Button
              href={href}
              {...link}
              variant="outline"
              className="mt-[25px] min-w-[125px] max-md:mb-[25px] max-[479px]:mb-[15px]"
            >
              {cta}
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}
